import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type ClimaPesquisaResumo } from "../api";
import type { Row, TabDef } from "../types";
import { classificacaoClima } from "../clima-modelo";
import CrudView from "./CrudView";
import ClimaPesquisaModal from "./ClimaPesquisaModal";
import ErrorState from "./ErrorState";
import { IconClose, IconPlus, IconSearch, IconTrash } from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * PESQUISA DE CLIMA ORGANIZACIONAL — a aba do RH.
 *
 * Duas visões:
 *   • "Pesquisas"   → o questionário oficial da SERCAMP aplicado por LINK: um
 *                     link para cada colaborador, o acompanhamento de quem já
 *                     respondeu, cada resposta pergunta a pergunta e o
 *                     consolidado.
 *   • "Lançamentos" → o histórico de clima que já existia (tabela
 *                     `engajamento`), que continua alimentando o dashboard e a
 *                     IA de ações. Cada resposta recebida por link entra aqui
 *                     automaticamente, então nada foi perdido.
 */

type Visao = "pesquisas" | "lancamentos";

function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

interface Props {
  tab: TabDef;
  colaboradores: Row[];
  onDataChanged: () => void;
}

export default function PesquisaClimaView({ tab, colaboradores, onDataChanged }: Props) {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [visao, setVisao] = useState<Visao>("pesquisas");
  const [linhas, setLinhas] = useState<ClimaPesquisaResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [nova, setNova] = useState(false);
  const [abrindo, setAbrindo] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setLinhas(await api.clima.list());
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtradas = useMemo(() => {
    const q = norm(busca);
    if (!q) return linhas;
    return linhas.filter((p) => norm(p.titulo).includes(q) || norm(p.periodo).includes(q));
  }, [linhas, busca]);

  async function excluir(p: ClimaPesquisaResumo) {
    if (
      !confirm(
        `Excluir a pesquisa "${p.titulo}"?\n\n` +
          `Os links e as respostas dela também são apagados. ` +
          `Os lançamentos já gerados no histórico de clima continuam.`,
      )
    )
      return;
    try {
      await api.clima.remove(p.id);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  if (visao === "lancamentos") {
    return (
      <div className="clima">
        <Abas visao={visao} setVisao={setVisao} />
        <CrudView tab={tab} colaboradores={colaboradores} onDataChanged={onDataChanged} />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="clima">
        <Abas visao={visao} setVisao={setVisao} />
        <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />
      </div>
    );
  }

  return (
    <div className="clima">
      <Abas visao={visao} setVisao={setVisao} />

      <div className="crud">
        <div className="crud-toolbar">
          <div className="search-wrap">
            <IconSearch size={17} />
            <input
              className="search"
              placeholder="Buscar pesquisa ou período..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="toolbar-right">
            <span className="badge">{filtradas.length} pesquisa(s)</span>
            <button className="btn-primary" onClick={() => setNova(true)}>
              <IconPlus size={17} /> Nova pesquisa
            </button>
          </div>
        </div>

        {aviso && (
          <div className="endo-aviso">
            <span>{aviso}</span>
            <button className="icon-btn" title="Fechar" onClick={() => setAviso(null)}>
              <IconClose size={16} />
            </button>
          </div>
        )}

        <div className="table-wrap">
          {loading ? (
            <div className="empty">Carregando...</div>
          ) : filtradas.length === 0 ? (
            <div className="empty">
              Nenhuma pesquisa ainda. Clique em <strong>Nova pesquisa</strong>: o questionário
              oficial da SERCAMP já vem pronto (setor, tempo de empresa, as 9 afirmações de 1 a 5 e
              a pergunta aberta). Depois é só gerar o link de cada colaborador.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Pesquisa</th>
                  <th>Período</th>
                  <th>Perguntas</th>
                  <th>Respostas</th>
                  <th>Média</th>
                  <th>Sigilo</th>
                  <th>Situação</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((p) => {
                  const classe = classificacaoClima(p.media);
                  return (
                    <tr key={p.id}>
                      <td>
                        <button className="triagem-nome" onClick={() => setAbrindo(p.id)}>
                          {p.titulo}
                        </button>
                        <small className="muted"> · criada em {quando(p.criado_em)}</small>
                      </td>
                      <td>{p.periodo || "—"}</td>
                      <td>{p.perguntas?.length ?? 0}</td>
                      <td>
                        {p.respostas} de {p.convites}
                        {p.convites > 0 && (
                          <small className="muted">
                            {" "}
                            · {Math.round((p.respostas / p.convites) * 100)}%
                          </small>
                        )}
                      </td>
                      <td>
                        {p.media != null ? (
                          <span className={`pill pill-${classe.tom}`}>
                            {p.media.toFixed(2)}/5 · {classe.rotulo}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{p.identificada ? "Identificada" : "Anônima"}</td>
                      <td>
                        <span className={`pill ${p.status === "Aberta" ? "pill-ok" : "pill-neutral"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="col-actions">
                        <button className="btn-secondary btn-sm" onClick={() => setAbrindo(p.id)}>
                          Abrir
                        </button>
                        {podeExcluir && (
                          <button
                            className="icon-btn icon-btn-danger"
                            title="Excluir pesquisa"
                            onClick={() => excluir(p)}
                          >
                            <IconTrash size={17} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {nova && (
        <ClimaPesquisaModal
          colaboradores={colaboradores}
          onClose={() => {
            setNova(false);
            carregar();
          }}
          onSaved={async (msg) => {
            setAviso(msg);
            await carregar();
          }}
        />
      )}

      {abrindo != null && (
        <ClimaPesquisaModal
          id={abrindo}
          colaboradores={colaboradores}
          onClose={() => {
            setAbrindo(null);
            carregar();
          }}
          onSaved={async (msg) => {
            setAviso(msg);
            await carregar();
            // A resposta da pesquisa também vira lançamento de clima: o
            // dashboard precisa saber.
            onDataChanged();
          }}
        />
      )}
    </div>
  );
}

function Abas({ visao, setVisao }: { visao: Visao; setVisao: (v: Visao) => void }) {
  return (
    <div className="endo-abas">
      <button className={visao === "pesquisas" ? "ativa" : ""} onClick={() => setVisao("pesquisas")}>
        Pesquisas por link
      </button>
      <button
        className={visao === "lancamentos" ? "ativa" : ""}
        onClick={() => setVisao("lancamentos")}
      >
        Lançamentos (histórico)
      </button>
    </div>
  );
}
